import React from 'react';

interface Props {
  id: string;
}

const ModernSlaveryActTransparencyStatement: React.FC<Props> = ({ id }) => {
  return (
    <>
      <h4 id={id} data-magellan-target={id}>
        Modern Slavery Act Transparency Statement
      </h4>
      <h5>Introduction</h5>
      <p>
        At Plaid, we are committed to a set of core principles: Impact; Grow
        together; Embrace openness and positivity; Think rigorously, act with
        urgency; Make it better; Consumer outcomes via customer love; and Invent
        tomorrow. In accordance with our core principles, Plaid is committed to
        ensuring there is no modern slavery or human trafficking in our
        businesses and supply chains.
      </p>
      <p>
        In compliance with its obligation under s.54 of the Modern Slavery Act
        2015 (the <strong>"2015 Act"</strong>), this statement sets out the
        steps Plaid Inc. has taken in the last financial year to ensure modern
        slavery and human trafficking is not taking place in any parts of its
        business or in any of its supply chains. This statement covers the steps
        taken by Plaid Inc. and all of its group subsidiaries, including Plaid
        Financial Ltd. and Plaid, B.V. (collectively, <strong>"Plaid"</strong>{' '}
        and <strong>“our”</strong>). Although covered by this statement, we note
        that Plaid Inc.’s wholly-owned subsidiary, Plaid Financial Ltd., does
        not have an obligation itself to make a transparency in supply statement
        pursuant to s.54 of the 2015 Act.
      </p>
      <h5>Our business</h5>
      <p>
        Plaid provides technical infrastructure application programming
        interfaces (<strong>"APIs"</strong>) to companies that develop digital
        financial applications (<strong>"apps"</strong>) and services to enable
        consumers and businesses (together, <strong>“end users”</strong>) to
        share data from their financial accounts with the apps and services.
        These apps and services help end users do things like save for
        retirement, manage their spending, streamline loan applications, or
        transfer money.
      </p>
      <p>
        Plaid is a leading provider in the UK, Europe and North America,
        connecting over 4,500 apps and services to more than 11,000 financial
        institutions.
      </p>
      <p>
        Plaid is headquartered in San Francisco, California and has over 600
        team members across 6 offices in the US, Europe and the UK.
      </p>
      <p>
        In the UK, Plaid is authorised by the Financial Conduct Authority under
        the Payment Services Regulations 2017 (Firm Reference Number: 804718)
        for the provision of payment services.
      </p>
      <h5>Our supply chains</h5>
      <p>
        As a technology company, Plaid's substantive supply chains are
        relatively short, and mostly comprised of organisations Plaid uses on an
        on-going basis for services such as Software as a Service (
        <strong>"SaaS"</strong>) or client installable software. In accordance
        with our Vendor Management Policy introduced in 28 May 2019 and most
        recently updated 26 June 2020, during last financial year, we have
        continued to undertake an on-boarding review of new vendors as well as
        ongoing monitoring of critical vendors to ensure they are complying with
        their contractual commitments and not otherwise breaching applicable
        law. Plaid’s relationship with a vendor may be adjusted or terminated in
        the event that the monitoring reveals deficiencies or material risks
        that are unacceptable to Plaid. This would include any identified risk
        of modern slavery or human trafficking. We have not identified any
        potential modern slavery or human trafficking issues to date in our
        supply chains.
      </p>
      <h5>Our policies and contractual controls</h5>
      <p>
        Plaid strictly complies with applicable laws and regulations, as well as
        our internal policies and procedures, including our Code of Business
        Conduct (<strong>"the Code"</strong>), to ensure we act professionally,
        transparently and with integrity at all times.
      </p>
      <p>
        The Code exists to help make clear what we expect of our team members,
        and to make sure that Plaid continues to be a company that others want
        to do business with and that our team members are proud to be part of.
        All team members, as well as those doing business with Plaid, must abide
        by and follow this Code, which covers the laws we must obey and offers
        guidance for navigating the complexities of doing business on a global
        scale.
      </p>
      <p>
        In relation to human trafficking the Code states "The United States
        Government has a zero-tolerance policy regarding trafficking in persons.
        It goes without saying that Plaid strictly prohibits team members from
        soliciting or engaging in: (a) prostitution, (b) trafficking in persons,
        and (c) forced labor. Plaid will take appropriate disciplinary action —
        including, but not limited to, termination — against anyone who violates
        this prohibition. Team members are also under an affirmative obligation
        to timely report any information received from any source potentially
        constituting a violation of this policy."
      </p>
      <p>
        Plaid staff are required to read and sign an 'Acknowledgement of Receipt
        and Review' to confirm they received a copy of the Code and understand
        and will act in accordance with the rules, policies and procedures in
        the Code. The Code has continued to apply during the last financial
        year. It is reviewed and updated (as needed) on an annual basis and was
        last reviewed and updated on 8 June 2020. Compliance with the Code
        should, amongst other things, help reduce the risks of modern slavery
        and human trafficking in the Plaid business and its supply chains.
      </p>
      <h5>Risk assessment and due diligence</h5>
      <p>
        Plaid is not aware of any slavery or human trafficking issues within its
        group businesses or supply chains. In the last financial year, no
        potential issues of this type have been identified or reported through
        internal reporting channels (such as Plaid's procedures for reporting
        suspected violations, illegal or unethical behavior outlined in the
        Code), audit processes, other whistleblowing channels or otherwise.
        Plaid Inc. has not received any information from any of the entities in
        the Plaid group indicating that there may be any such issues with any of
        its intra-group suppliers or their supply chains.
      </p>
      <p>
        Reflecting upon the position as set out above, we consider the risk of
        slavery or human trafficking in Plaid, and its supply chain continues to
        be low. However, should any potential issue be identified, Plaid will
        act swiftly to investigate and, where necessary, act to ensure there is
        no modern slavery or human trafficking occurring in its business or
        supply chains.
      </p>
      <h5>Next steps</h5>
      <p>
        Plaid is committed to continuing to prevent slavery or human trafficking
        in its business and supply chain.
      </p>
      <h5>Approval of this statement</h5>
      <p>
        This statement is made in compliance with Plaid Inc.'s obligations under
        s.54 of the Modern Slavery Act 2015 for the financial year ending
        December 31, 2020. This statement was approved by Plaid Inc. on 30 April
        2021.
      </p>
    </>
  );
};

export default ModernSlaveryActTransparencyStatement;
